import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward, Settings,
  CheckCircle, BookOpen, StickyNote, Trophy
} from 'lucide-react';

const chapters = [
  { id: 1, title: 'Introduction', time: '00:00', completed: true },
  { id: 2, title: 'Safety checklist', time: '02:30', completed: true },
  { id: 3, title: 'Rigging procedure', time: '05:15', completed: false, current: true },
  { id: 4, title: 'Connection sequence', time: '08:45', completed: false },
  { id: 5, title: 'Final checks', time: '11:20', completed: false },
];

const quizOptions = [
  '8,500 ft-lbs',
  '12,000 ft-lbs',
  '15,000 ft-lbs',
  '10,500 ft-lbs',
];
const correctAnswer = 3;

export default function TrainingVideoPlayer() {
  const navigate = useNavigate();
  const { videoId: _videoId } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'chapters' | 'notes' | 'resources'>('chapters');
  const [note, setNote] = useState('');

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Power Tongs Setup</h1>
        </div>
      </div>

      {/* Video Area */}
      <div className="relative aspect-video bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs text-fieldiq-muted mb-2">Video Player</p>
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-16 h-16 rounded-full bg-fieldiq-accent/90 flex items-center justify-center"
          >
            {isPlaying ? <Pause className="w-7 h-7 text-fieldiq-dark" /> : <Play className="w-7 h-7 text-fieldiq-dark ml-1" />}
          </button>
        </div>
        {/* Progress overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-2">
          <div className="w-full bg-white/20 rounded-full h-1">
            <div className="bg-fieldiq-accent h-1 rounded-full" style={{ width: '67%' }} />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-white/70">5:15</span>
            <span className="text-[10px] text-white/70">12:45</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-fieldiq-card border-b border-fieldiq-border px-4 py-2 flex items-center justify-center gap-6">
        <button className="p-2"><SkipBack className="w-5 h-5 text-white" /></button>
        <button onClick={() => setIsPlaying(!isPlaying)} className="w-10 h-10 rounded-full bg-fieldiq-accent flex items-center justify-center">
          {isPlaying ? <Pause className="w-5 h-5 text-fieldiq-dark" /> : <Play className="w-5 h-5 text-fieldiq-dark ml-0.5" />}
        </button>
        <button className="p-2"><SkipForward className="w-5 h-5 text-white" /></button>
        <button className="p-2"><Settings className="w-4 h-4 text-fieldiq-muted" /></button>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* Quiz Popup */}
        {!showQuiz && (
          <button
            onClick={() => setShowQuiz(true)}
            className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl py-2.5 text-xs text-white font-medium hover:border-fieldiq-accent transition-colors"
          >
            Take Quiz Question (Chapter 3)
          </button>
        )}
        {showQuiz && (
          <div className="bg-fieldiq-card border border-fieldiq-accent/30 rounded-xl p-3 space-y-2">
            <p className="text-xs font-semibold text-white">Quiz: Chapter 3</p>
            <p className="text-xs text-fieldiq-text">What's the maximum torque setting for 7" L-80 casing?</p>
            <div className="space-y-1.5">
              {quizOptions.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => setQuizAnswer(i)}
                  className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors ${
                    quizAnswer === i
                      ? i === correctAnswer
                        ? 'border-green-500 bg-green-500/10 text-green-400'
                        : 'border-red-500 bg-red-500/10 text-red-400'
                      : 'border-fieldiq-border bg-fieldiq-dark text-white'
                  }`}
                >
                  {opt}
                  {quizAnswer === i && i === correctAnswer && ' — Correct!'}
                  {quizAnswer === i && i !== correctAnswer && ' — Try again'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-fieldiq-dark rounded-lg p-0.5">
          {([
            { key: 'chapters' as const, label: 'Chapters', icon: BookOpen },
            { key: 'notes' as const, label: 'Notes', icon: StickyNote },
            { key: 'resources' as const, label: 'Resources', icon: BookOpen },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md text-[10px] font-semibold transition-colors ${
                activeTab === tab.key ? 'bg-fieldiq-card text-white shadow-sm' : 'text-fieldiq-muted'
              }`}
            >
              <tab.icon className="w-3 h-3" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'chapters' && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {chapters.map((ch) => (
              <button key={ch.id} className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${ch.current ? 'bg-fieldiq-accent/10' : ''}`}>
                {ch.completed ? (
                  <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                ) : ch.current ? (
                  <Play className="w-4 h-4 text-fieldiq-accent shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-fieldiq-muted/30 shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-xs font-medium ${ch.current ? 'text-fieldiq-accent' : 'text-white'}`}>{ch.title}</p>
                  <p className="text-[10px] text-fieldiq-muted">{ch.time}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-2">
            <textarea
              placeholder="Add your notes here..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 text-xs text-white placeholder-fieldiq-muted min-h-[100px] focus:border-fieldiq-accent focus:outline-none resize-none"
            />
            <button className="w-full bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold py-2 rounded-lg hover:bg-amber-400 transition-colors">
              Save Note
            </button>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
            {['Power Tongs Manual (PDF)', 'Torque Chart Reference', 'Safety Data Sheet'].map((doc) => (
              <div key={doc} className="flex items-center gap-3 px-3 py-2.5">
                <BookOpen className="w-4 h-4 text-fieldiq-accent shrink-0" />
                <p className="text-xs font-medium text-white">{doc}</p>
              </div>
            ))}
          </div>
        )}

        {/* Certification Badge */}
        <div className="bg-fieldiq-accent/10 border border-fieldiq-accent/30 rounded-xl p-3 flex items-center gap-3">
          <Trophy className="w-6 h-6 text-fieldiq-accent shrink-0" />
          <div>
            <p className="text-xs font-semibold text-white">Complete to earn badge</p>
            <p className="text-[10px] text-fieldiq-text">Finish all chapters & pass quiz</p>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 flex items-center justify-center gap-1 bg-fieldiq-card border border-fieldiq-border text-white text-xs font-medium py-2.5 rounded-xl hover:border-fieldiq-accent transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button className="flex-1 flex items-center justify-center gap-1 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold py-2.5 rounded-xl hover:bg-amber-400 transition-colors">
            <CheckCircle className="w-3.5 h-3.5" /> Mark Complete
          </button>
          <button className="flex items-center justify-center bg-fieldiq-card border border-fieldiq-border text-white py-2.5 px-3 rounded-xl hover:border-fieldiq-accent transition-colors">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
